<template>
  <div class="pipeline-stage" v-show="isVisible">
    <h3>{{ title }}</h3>
    
    <!-- Action buttons -->
    <button 
      v-for="action in actions" 
      :key="action.id"
      class="action-btn"
      :class="{ secondary: action.secondary, 'upgrade-required': action.requiresUpgrade }"
      :style="{ display: action.visible ? 'block' : 'none' }"
      @click="action.handler"
      :title="action.tooltip"
    >
      {{ action.text }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useGameStore } from '@/stores/gameStore';
import { createActionBuilder } from '@/utils/actionBuilder';
import type { Action } from '@/types';

interface Props {
  title: string;
  stage: 'dataGeneration' | 'dataProcessing' | 'learning' | 'consciousness';
}

const props = defineProps<Props>();
const store = useGameStore();
const actionBuilder = createActionBuilder();

const isVisible = computed(() => {
  switch (props.stage) {
    case 'dataGeneration':
      // Always visible - this is the starting stage
      return true;
    
    case 'dataProcessing':
      return store.hasUpgrade('dataCleaning');
    
    case 'learning':
      return store.hasUpgrade('ml');
    
    case 'consciousness':
      // Visible when reasoning systems upgrade is purchased
      return store.hasUpgrade('reasoningSystems');
    
    default:
      return true;
  }
});

const actions = computed<Action[]>(() => {
  const stageConfig = {
    infrastructure: store.entityManager.getPipelineStageInfrastructure(props.stage),
    actions: store.entityManager.getPipelineStageActions(props.stage)
  };
  if (!stageConfig) return [];

  return actionBuilder.buildStageActions(
    stageConfig.actions,
    stageConfig.infrastructure
  );
});
</script>

<style scoped>
.pipeline-stage {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.pipeline-stage h3 {
  color: #00ff88;
  margin-bottom: 15px;
  text-align: center;
}

.action-btn {
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
  background: linear-gradient(45deg, #00ff88, #00cc6a);
  color: #000;
  position: relative;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 255, 136, 0.3);
}

.action-btn.secondary {
  background: linear-gradient(45deg, #666, #444);
  color: #fff;
}

.action-btn.secondary:hover {
  background: linear-gradient(45deg, #777, #555);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
}

.action-btn[title]:hover::after {
  content: attr(title);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: pre-line;
  z-index: 1000;
  max-width: 200px;
  text-align: center;
}
</style> 